import { logger } from '../../../utils/logger'
import { getJsonSessionItem, SessionStorage } from '../../../utils/session-storage'
import type { ServiceHandler } from './service-registry'
import { getSessionStorageMetadataKey } from './setMetadata'
import type {
  GetMetadataItemRequest,
  GetMetadataItemResponse,
} from '@custom-embedded-code-sdk/methods/metadata'

/**
 * GET_METADATA_ITEM service handler
 *
 * Handles requests to retrieve a metadata item for the current campaign from session storage.
 * The session storage key is `metadata_{campaignId}`
 *
 * If metadata key does not exist, returns an error.
 */
export const getMetadataItem: ServiceHandler<
  GetMetadataItemRequest,
  GetMetadataItemResponse
> = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const campaignId = pageConfig.campaignId

  try {
    if (!campaignId) {
      throw new Error('CustomEmbeddedCode:getMetadataItem: Campaign ID not available')
    }

    const storageKey = getSessionStorageMetadataKey(campaignId)
    const metadata = getJsonSessionItem<SessionStorage>(storageKey) || {}
    const metadataItemKey = payload as keyof SessionStorage

    if (!(metadataItemKey in metadata)) {
      sendErrorResponse(iframeId, messageId, `Metadata item "${metadataItemKey}" does not exist`)
      return
    }

    const metadataItemValue = metadata[metadataItemKey] as GetMetadataItemResponse

    logger('info', 'CustomEmbeddedCode:getMetadataItem: Successfully retrieved metadata item', {
      context: { iframeId, messageId, campaignId, storageKey, metadataItemValue },
    })

    sendSuccessResponse(iframeId, messageId, metadataItemValue)
  } catch (error) {
    logger(
      'error',
      new Error('CustomEmbeddedCode:getMetadataItem: Failed to get metadata', { cause: error }),
      {
        context: { iframeId, messageId, campaignId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to get metadata')
  }
}
